import { useState } from 'react'

import { string, bool, func, arrayOf, shape, oneOfType, number } from 'prop-types'

import DragPreview from './drag-preview'
import { Button } from '../../button'

import useDualList from './use-dual-list'
import List from './list'

import s from './dual-list.module.scss'

const DualList = props => {
  const {
    unselectedTitle,
    selectedTitle,
    options,
    value,
    onChange,
    canFilter,
    filterPlaceholder,
    disabled,
    extraOptions,
    getSelectedOptionName,
    addAllParentsButtonTitle,
    addAllParentsButtonDesc,
    displayParentGroups = false,
    unGroupedTitle,
  } = props

  const [unselectedFilter, setUnselectedFilter] = useState('')
  const [selectedFilter, setSelectedFilter] = useState('')

  const {
    unselectedOptions,
    selectedOptions,
    itemsToAdd,
    itemsToRemove,
    handleSelectedItemsToAddChange,
    handleAddAllClick,
    handleAddAllParentsClick,
    isAddAllBtnDisabled,
    handleAddClick,
    isAddBtnDisabled,
    isAddAllParentsBtnDisabled,
    handleSelectedItemsToRemoveChange,
    handleRemoveClick,
    isRemoveAllBtnDisabled,
    handleRemoveAllClick,
    isRemoveBtnDisabled,
    handleDropUnselectedItems,
    handleDropSelectedItems,
    handleAddMultipleItems,
    handleRemoveMultipleItems,
  } = useDualList({options, value, onChange, disabled, unselectedFilter, selectedFilter})

  const hasParents = options.some(
    option => Array.isArray(option.parents) && option.parents.length > 0
  )

  return (
    <>
      <DragPreview />

      <div className={s['dual-list-wrapper']}>
        <List
          canFilter={canFilter}
          filterPlaceholder={filterPlaceholder}
          disabled={disabled}
          options={unselectedOptions}
          displayParentGroups={displayParentGroups}
          unGroupedTitle={unGroupedTitle}
          value={itemsToAdd}
          onChange={handleSelectedItemsToAddChange}
          handleAddGroupItems={handleAddMultipleItems}
          title={unselectedTitle}
          handleDropItems={handleDropSelectedItems}
          extraOptions={extraOptions}
          filter={unselectedFilter}
          setFilter={setUnselectedFilter}
        />

        <div className={s['dual-list-controll-wrapper']}>
          {addAllParentsButtonTitle && hasParents && (
            <Button
              type="secondary"
              onClick={handleAddAllParentsClick}
              disabled={isAddAllParentsBtnDisabled}
              buttonType="button"
              title={addAllParentsButtonDesc}
            >
              {addAllParentsButtonTitle}
            </Button>
          )}
          <Button 
            type="secondary" 
            onClick={handleAddAllClick} 
            disabled={isAddAllBtnDisabled} 
            buttonType="button"
            title="Add all available items, excluding items that don't match the filter if applied"
          >
            Add All
          </Button>
          <Button 
            type="secondary" 
            onClick={handleAddClick} 
            disabled={isAddBtnDisabled} 
            buttonType="button"
            title="Add highlighted item(s)"
          >
            Add
          </Button>
          <Button 
            type="secondary" 
            onClick={handleRemoveClick} 
            disabled={isRemoveBtnDisabled} 
            buttonType="button"
            title="Remove highlighted item(s)"
          >
            Remove
          </Button>
          <Button 
            type="secondary" 
            onClick={handleRemoveAllClick} 
            disabled={isRemoveAllBtnDisabled} 
            buttonType="button"
            title="Remove all selected items, excluding items that don't match the filter if applied"
          >
            Remove All
          </Button>
        </div>

        <List
          canFilter={canFilter}
          filterPlaceholder={filterPlaceholder}
          disabled={disabled}
          options={selectedOptions}
          displayParentGroups={displayParentGroups}
          unGroupedTitle={unGroupedTitle}
          value={itemsToRemove}
          onChange={handleSelectedItemsToRemoveChange}
          handleRemoveGroupItems={handleRemoveMultipleItems}
          title={selectedTitle}
          handleDropItems={handleDropUnselectedItems}
          extraOptions={extraOptions}
          getSelectedOptionName={getSelectedOptionName}
          filter={selectedFilter}
          setFilter={setSelectedFilter}
        />
      </div>
    </>
  )
}

DualList.defaultProps = {
  options: [],
  value: [],
  getSelectedOptionName: ({ label, value }) => label,
}

DualList.propTypes = {
  unselectedTitle: string,
  selectedTitle: string,
  options: arrayOf(
    shape({
      label: oneOfType([string, number]),
      value: oneOfType([string, number]),
      parents: arrayOf(shape({
        label: oneOfType([string, number]),
        value: oneOfType([string, number]),
      })),
    }),
  ).isRequired,
  value: arrayOf(oneOfType([string, number])),
  onChange: func,
  canFilter: bool,
  disabled: bool,
  getSelectedOptionName: func,
  addAllParentsButtonTitle: string,
  addAllParentsButtonDesc: string,
  displayParentGroups: bool,
  unGroupedTitle: string,
}

export default DualList
